"use client";

import Link from "next/link";
import { BookOpen, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PrefToggles } from "@/components/prefs/pref-toggles";
import { usePrefs } from "@/components/providers/app-providers";

export default function HomePage() {
  const { t } = usePrefs();

  const features = [
    { title: t("featureDevicesTitle"), body: t("featureDevicesBody") },
    { title: t("featurePairTitle"), body: t("featurePairBody") },
    { title: t("featureControlTitle"), body: t("featureControlBody") },
    { title: t("featureApiTitle"), body: t("featureApiBody") },
  ];

  return (
    <div className="flex min-h-full flex-col">
      <header className="mx-auto flex w-full max-w-6xl items-center justify-between gap-2 px-3 py-4 sm:px-6 sm:py-5">
        <Link href="/" className="flex shrink-0 items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-teal-600 text-white shadow-sm">
            <MessageCircle className="h-4 w-4" />
          </span>
          <span className="hidden font-[family-name:var(--font-manrope)] text-lg font-bold tracking-tight text-slate-950 min-[440px]:inline dark:text-white">
            {t("brand")}
          </span>
        </Link>
        <div className="flex min-w-0 items-center gap-0.5 sm:gap-2">
          <PrefToggles className="flex items-center" />
          <Button asChild variant="ghost" size="icon" className="sm:w-auto sm:px-3">
            <Link href="/api" aria-label={t("apiDocsLink")} title={t("apiDocsLink")}>
              <BookOpen className="h-4 w-4" />
              <span className="hidden sm:inline">{t("apiDocsLink")}</span>
            </Link>
          </Button>
          <Button asChild variant="ghost" className="h-9 px-2.5 sm:h-10 sm:px-4">
            <Link href="/login">{t("login")}</Link>
          </Button>
          <Button asChild className="h-9 px-3 sm:h-10 sm:px-4">
            <Link href="/register">{t("register")}</Link>
          </Button>
        </div>
      </header>

      <main className="mx-auto flex w-full max-w-6xl flex-1 flex-col px-4 pb-16 pt-10 sm:px-6 sm:pt-16">
        <section className="relative overflow-hidden rounded-[2rem] border border-slate-200/80 bg-white/80 px-6 py-14 shadow-sm dark:border-slate-700 dark:bg-slate-900/80 sm:px-12 sm:py-20">
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 bg-[linear-gradient(135deg,rgba(15,118,110,0.08),transparent_45%),radial-gradient(circle_at_80%_20%,rgba(15,23,42,0.08),transparent_40%)] dark:bg-[linear-gradient(135deg,rgba(45,212,191,0.08),transparent_45%),radial-gradient(circle_at_80%_20%,rgba(148,163,184,0.08),transparent_40%)]"
          />
          <div className="relative max-w-2xl">
            <p className="font-[family-name:var(--font-manrope)] text-4xl font-bold tracking-tight text-slate-950 dark:text-white sm:text-5xl">
              {t("brand")}
            </p>
            <h1 className="mt-4 text-2xl font-semibold leading-snug text-slate-900 dark:text-slate-50 sm:text-3xl">
              {t("heroTitle")}
            </h1>
            <p className="mt-4 max-w-xl text-base leading-relaxed text-slate-600 dark:text-slate-300 sm:text-lg">
              {t("heroBody")}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild size="lg">
                <Link href="/register">{t("ctaStart")}</Link>
              </Button>
              <Button asChild size="lg" variant="outline">
                <Link href="/login">{t("ctaLogin")}</Link>
              </Button>
            </div>
          </div>
        </section>

        <section className="mt-14 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          {features.map((f) => (
            <div key={f.title} className="space-y-2">
              <h2 className="text-base font-semibold text-slate-900 dark:text-slate-50">{f.title}</h2>
              <p className="text-sm leading-relaxed text-slate-600 dark:text-slate-300">{f.body}</p>
            </div>
          ))}
        </section>
      </main>

      <footer className="border-t border-slate-200/80 py-6 text-center text-sm text-slate-500 dark:border-slate-800 dark:text-slate-400">
        © {new Date().getFullYear()} {t("brand")}
      </footer>
    </div>
  );
}
